import { searchChords } from './chordsSearchClient'
import { searchLyrics } from './lyricsSearchClient'
import { searchNotation } from './notationSearchClient'
import { researchTuneBackground } from './tuneBackgroundResearchClient'
import { applyGeneratedBackgroundInfo } from './viewModeUtils'
import { unwrapSearchResult } from './searchResultUtils'
import { setPlainLyricLines } from './wLinesUtils'
import { importedTuneFromNotationCandidate } from './notationImportUtils'
import { enqueueStemCreateJob } from './stemCreateQueue'
import { areStemBulkOperationsEnabled } from './stemBulkOperations'
import { resolveActiveLinkForTune } from './mediaLinkResolve'
import { syncTuneFromStore } from './bulkCheckTuneSync'
import { capitalizeSongTitle } from './titleCaseUtils'
import {
  applyStructureFix,
  previewStructureFix,
  STRUCTURE_FIX_ACTIONS,
} from './tuneAbcStructureFix'
import { fixTuneAbcHeaders } from './tuneAbcCorrectnessCheck'

function tuneTitle(tune) {
  return tune && tune.name ? String(tune.name).trim() : ''
}

function tuneArtist(tune) {
  return tune && tune.composer ? String(tune.composer).trim() : ''
}

function getPrimaryLink(tune, tunebook) {
  const isYoutubeLink = tunebook && tunebook.utils ? tunebook.utils.isYoutubeLink : null
  return resolveActiveLinkForTune(tune, null, isYoutubeLink)
}

export function tuneHasAudioForFix(tune, tunebook) {
  return !!getPrimaryLink(tune, tunebook)
}

function saveFixTune(tune, tunebook, opts) {
  const synced = syncTuneFromStore(tune, opts)
  tunebook.saveTune(synced, false, { historyLabel: 'Bulk check fix', immediate: true })
  return syncTuneFromStore(synced, opts)
}

async function searchChordsAndLyricsForTune(tune, tunebook, token, signal, fixOpts) {
  const title = tuneTitle(tune)
  const artist = tuneArtist(tune)
  const searchOpts = {
    title: title,
    artist: artist,
    accessToken: token,
    signal: signal,
    resolverAvailable: fixOpts && fixOpts.resolverAvailable,
    abcTools: tunebook && tunebook.abcTools ? tunebook.abcTools : null,
    renderChords: fixOpts && typeof fixOpts.renderChords === 'function' ? fixOpts.renderChords : null,
    preferRemoteChords: true,
    skipLocalChords: true,
    skipColdIndexLoad: true,
    forceResolver: !(fixOpts && fixOpts.resolverAvailable === false),
  }
  try {
    const chordResult = unwrapSearchResult(await searchChords(searchOpts))
    return {
      chordText: chordResult.chordText || '',
      lyricLines: Array.isArray(chordResult.lyricLines) ? chordResult.lyricLines : [],
      artist: chordResult.artist || artist,
    }
  } catch (chordError) {
    if (chordError && chordError.name === 'AbortError') throw chordError
    try {
      const lyricResult = unwrapSearchResult(await searchLyrics(Object.assign({}, searchOpts, {
        skipColdIndexLoad: true,
      })))
      return {
        chordText: '',
        lyricLines: Array.isArray(lyricResult.lines)
          ? lyricResult.lines
          : String(lyricResult.text || '').replace(/\r\n/g, '\n').split('\n'),
        artist: lyricResult.artist || artist,
      }
    } catch (lyricError) {
      if (lyricError && lyricError.name === 'AbortError') throw lyricError
      return { chordText: '', lyricLines: [], artist: artist }
    }
  }
}

function applyChordsLyricsToTune(tune, result) {
  const next = Object.assign({}, tune)
  if (result.artist && !next.composer) next.composer = result.artist
  if (Array.isArray(result.lyricLines) && result.lyricLines.length > 0) {
    setPlainLyricLines(next, result.lyricLines)
  }
  if (result.chordText && result.chordText.trim()) {
    next.timingScaffold = true
    if (!next.voices) next.voices = { '1': { notes: [] } }
    const voiceKey = Object.keys(next.voices)[0] || '1'
    if (!next.voices[voiceKey]) next.voices[voiceKey] = { notes: [] }
    if (!Array.isArray(next.voices[voiceKey].notes) || next.voices[voiceKey].notes.length === 0) {
      next.voices[voiceKey].notes = ['z z z z |']
    }
  }
  return next
}

async function searchAbcForTune(tune, tunebook, token, signal, fixOpts) {
  const result = await searchNotation({
    title: tuneTitle(tune),
    artist: tuneArtist(tune),
    accessToken: token,
    signal: signal,
    resolverAvailable: fixOpts && fixOpts.resolverAvailable,
    abcTools: tunebook && tunebook.abcTools ? tunebook.abcTools : null,
  })
  if (result.multiple && Array.isArray(result.candidates) && result.candidates.length > 0) {
    const candidate = result.candidates[0]
    if (candidate && candidate.abc) {
      return importedTuneFromNotationCandidate(tunebook.abcTools, candidate.abc, candidate)
    }
  }
  if (result && result.abc) {
    return importedTuneFromNotationCandidate(tunebook.abcTools, result.abc, result)
  }
  throw new Error('No ABC notation found')
}

function mergeImportedTune(original, imported) {
  const next = Object.assign({}, original)
  if (imported.name && !next.name) next.name = imported.name
  if (imported.composer && !next.composer) next.composer = imported.composer
  if (imported.meter && !next.meter) next.meter = imported.meter
  if (imported.key && !next.key) next.key = imported.key
  if (imported.tempo && !next.tempo) next.tempo = imported.tempo
  if (imported.noteLength && !next.noteLength) next.noteLength = imported.noteLength
  if (imported.voices) next.voices = imported.voices
  if (imported.rhythm && !next.rhythm) next.rhythm = imported.rhythm
  return next
}

async function runAnalyseForTune(next, opts, tunebook) {
  const linkInfo = getPrimaryLink(next, tunebook)
  if (!linkInfo) throw new Error('No audio link to analyse')
  const link = Array.isArray(next.links) ? next.links[linkInfo.linkIndex] : null
  if (!link) throw new Error('No audio link to analyse')

  if (typeof opts.maybeAutoScan === 'function') {
    await opts.maybeAutoScan(next.id, linkInfo.linkIndex, link, {
      force: true,
      currentLinks: next.links,
    })
    next = syncTuneFromStore(next, opts)
  }

  if (typeof opts.runMediaAnalysis === 'function') {
    await opts.runMediaAnalysis(next, {
      force: true,
      linkIndex: linkInfo.linkIndex,
    })
    next = syncTuneFromStore(next, opts)
  } else if (typeof opts.maybeAutoScan !== 'function') {
    throw new Error('Playback region scan is not available')
  }

  return next
}

export async function runBulkCheckFixAction(action, options) {
  const opts = options || {}
  const tune = opts.tune
  const tunebook = opts.tunebook
  const token = opts.token
  const signal = opts.signal
  if (!tune || !tunebook) return tune

  let next = syncTuneFromStore(Object.assign({}, tune), opts)

  if (action === 'analyse') {
    return runAnalyseForTune(next, opts, tunebook)
  }

  if (action === 'searchAbc') {
    const imported = await searchAbcForTune(next, tunebook, token, signal, opts)
    next = mergeImportedTune(next, imported)
    return syncTuneFromStore(next, opts)
  }

  if (action === 'searchChordsLyrics') {
    const result = await searchChordsAndLyricsForTune(next, tunebook, token, signal, opts)
    next = applyChordsLyricsToTune(next, result)
    return syncTuneFromStore(next, opts)
  }

  if (action === 'searchArtist') {
    const result = await searchChordsAndLyricsForTune(next, tunebook, token, signal, opts)
    if (result.artist && String(result.artist).trim()) {
      next.composer = String(result.artist).trim()
    } else {
      throw new Error('No artist found')
    }
    return syncTuneFromStore(next, opts)
  }

  if (action === 'backgroundInfo') {
    const bg = await researchTuneBackground({
      title: tuneTitle(next),
      artist: tuneArtist(next),
      backgroundInfo: typeof next.backgroundInfo === 'string' ? next.backgroundInfo : '',
      accessToken: token,
      signal: signal,
    })
    if (bg && bg.text) applyGeneratedBackgroundInfo(next, bg.text)
    return syncTuneFromStore(next, opts)
  }

  if (action === 'capitalizeTitle') {
    if (next.name) {
      next.name = capitalizeSongTitle(next.name)
    }
    return syncTuneFromStore(next, opts)
  }

  if (action === 'scanLinkRegion') {
    const linkIndex = opts.linkIndex != null ? opts.linkIndex : 0
    const links = Array.isArray(next.links) ? next.links : []
    const link = links[linkIndex]
    if (!link || !String(link.link || '').trim()) {
      throw new Error('No link URL to scan')
    }
    if (typeof opts.maybeAutoScan !== 'function') {
      throw new Error('Playback region scan is not available')
    }
    await opts.maybeAutoScan(next.id, linkIndex, link, {
      force: true,
      currentLinks: links,
    })
    return syncTuneFromStore(next, opts)
  }

  if (action === 'stems') {
    if (!areStemBulkOperationsEnabled()) {
      throw new Error('Bulk stem generation is disabled to limit external API cost. Use Analyse in Media Controls for one tune at a time.')
    }
    const linkInfo = getPrimaryLink(next, tunebook)
    if (!linkInfo) throw new Error('No audio link for stems')
    const src = String(linkInfo.src || '').trim()
    enqueueStemCreateJob({
      tuneId: next.id,
      linkIndex: linkInfo.linkIndex,
      src: src,
      srcType: linkInfo.srcType || 'audio',
      tuneName: next.name || '',
      linkTitle: linkInfo.link.title || '',
      accessToken: token,
    })
    return next
  }

  const structureAction = STRUCTURE_FIX_ACTIONS.find(function(item) { return item.id === action })
  if (structureAction) {
    const abcTools = tunebook.abcTools
    const parseAndRender = opts.parseAndRender
    const fixed = applyStructureFix(action, next, abcTools, parseAndRender)
    if (!fixed) throw new Error('This fix could not be applied to the tune')
    return syncTuneFromStore(fixed, opts)
  }

  if (action === 'fixHeaders') {
    next = fixTuneAbcHeaders(next, tunebook.abcTools)
    return syncTuneFromStore(next, opts)
  }

  if (action === 'searchAll') {
    return syncTuneFromStore(next, opts)
  }

  return next
}

export const BULK_CHECK_FIX_ACTIONS = [
  { id: 'searchAll', label: 'Search All' },
  { id: 'analyse', label: 'Analyse', requiresAudio: true },
  { id: 'searchAbc', label: 'Search ABC' },
  { id: 'searchChordsLyrics', label: 'Search Chords And Lyrics' },
  { id: 'backgroundInfo', label: 'Background Info' },
  { id: 'stems', label: 'Stems', requiresAudio: true },
]

export { syncTuneFromStore, previewStructureFix, STRUCTURE_FIX_ACTIONS }
